import React, { Component } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import timeGridPlugin from "@fullcalendar/timegrid";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  Search,
} from "lucide-react";
import moment from "moment";
import { Link } from "react-router-dom";
import Appointment from "../components/Appointment.tsx";
import LeaveModal from "../components/LeaveModal.tsx";
import { Button } from "../components/ui/button.tsx";
import UserService from "../services/user.service";
import TechnicianService from "../services/technician.service";
import type {
  AppointmentItem,
  CalendarDisplaySettings,
  LeaveItem,
} from "./models";

type HomeState = {
  appointmentsData: AppointmentItem[];
  leavesData: LeaveItem[];
  showAppointment: boolean;
  currentAppointment: AppointmentItem | null;
  showLeaveModal: boolean;
  selectedLeave: LeaveItem | null;
  showLeaves: boolean;
  date: Date;
} & CalendarDisplaySettings;

export default class Home extends Component<Record<string, never>, HomeState> {
  interval?: ReturnType<typeof setInterval>;
  calendarRef = React.createRef<FullCalendar>();

  constructor(props: Record<string, never>) {
    super(props);

    this.state = {
      appointmentsData: [],
      leavesData: [],
      showAppointment: false,
      currentAppointment: null,
      showLeaveModal: false,
      selectedLeave: null,
      showLeaves: localStorage.getItem("showLeaves") !== "false",
      date: new Date(),
      calendarStartHour: 8,
      calendarEndHour: 18,
    };
  }

  hideModal = () => {
    this.setState({ showAppointment: false });
  };

  onAppointmentChanged = () => {
    this.getAppointmentsAndLeaves();
  };

  handleShowLeavesChangedFromEvent = () => {
    this.setState(
      {
        showLeaves: localStorage.getItem("showLeaves") !== "false",
      },
      () => {
        this.getAppointmentsAndLeaves();
      }
    );
  };

  handleLeavesUpdatedFromEvent = () => {
    this.getAppointmentsAndLeaves();
  };

  componentDidMount() {
    this.loadCalendarSettings();
    this.getAppointmentsAndLeaves();
    window.addEventListener(
      "showLeavesChanged",
      this.handleShowLeavesChangedFromEvent
    );
    window.addEventListener("leavesUpdated", this.handleLeavesUpdatedFromEvent);
    this.interval = setInterval(() => {
      this.getAppointmentsAndLeaves();
    }, 15000);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    window.removeEventListener(
      "showLeavesChanged",
      this.handleShowLeavesChangedFromEvent
    );
    window.removeEventListener(
      "leavesUpdated",
      this.handleLeavesUpdatedFromEvent
    );
  }

  loadCalendarSettings = async () => {
    try {
      const result = await UserService.getAccountSettings();
      this.setState({
        calendarStartHour: result.data.data?.calendarStartHour ?? 8,
        calendarEndHour: result.data.data?.calendarEndHour ?? 18,
      });
    } catch (error) {
      console.log(error);
    }
  };

  getAppointmentsAndLeaves = async (props?: Date) => {
    try {
      const date = props ? props : this.state.date;
      const firstDay = moment(
        new Date(date.getFullYear(), date.getMonth(), -14)
      )
        .toISOString()
        .replace("Z", "");
      const lastDay = moment(
        new Date(date.getFullYear(), date.getMonth() + 1, 14)
      )
        .toISOString()
        .replace("Z", "");

      const appointmentsPromise = UserService.getAppointments(
        firstDay,
        lastDay
      );
      const leavesPromise = this.state.showLeaves
        ? TechnicianService.getLeaves(firstDay, lastDay)
        : Promise.resolve({ data: { data: [] } });

      const [appointmentsResult, leavesResult] = await Promise.all([
        appointmentsPromise,
        leavesPromise,
      ]);

      const newAppointments = appointmentsResult.data.data || [];
      const newLeaves = (leavesResult.data.data || []).map((leave: any) => ({
        ...leave,
        isLeave: true,
      }));

      if (
        JSON.stringify(this.state.appointmentsData) !==
          JSON.stringify(newAppointments) ||
        JSON.stringify(this.state.leavesData) !== JSON.stringify(newLeaves)
      ) {
        this.setState({
          appointmentsData: newAppointments,
          leavesData: newLeaves,
        });
      }
    } catch (error) {
      if (typeof error === "object" && error !== null && "response" in error) {
        const apiError = error as { response?: { data?: unknown } };
        console.log(error);
        console.log(apiError.response?.data);
      }
    }
  };

  showModal = (props: any) => {
    if (props.isLeave) {
      this.setState({ showLeaveModal: true, selectedLeave: props });
    } else {
      this.setState({ showAppointment: true, currentAppointment: props });
    }
  };

  formatTitle = (event: any) => {
    if (event.isLeave) {
      return `Vacation: ${
        event.technicianName || event.technician_name || "Technician"
      }`;
    }
    const hour = moment(event.startDateTime).format("h a");
    if (event.status === "draft") {
      return `${hour}: available`;
    } else if (event.status === "day_off") {
      return `${hour}: ${event.note}`;
    } else {
      return `${hour}: ${event.client.clientName}`;
    }
  };

  viewChanged = (date: Date) => {
    if (this.state.date.getTime() === date.getTime()) {
      return;
    }
    this.setState({ date });
    this.getAppointmentsAndLeaves(date);
  };

  changeCalendarDate = (action: "prev" | "next" | "today") => {
    this.calendarRef.current?.getApi()[action]();
  };

  changeCalendarView = (
    viewName: "dayGridMonth" | "timeGridWeek" | "listWeek"
  ) => {
    this.calendarRef.current?.getApi().changeView(viewName);
  };

  eventClassNames = (event: any) => {
    if (event.isLeave) {
      return ["fc-event-leave"];
    }
    return [
      `fc-event-status-${String(event.status || "draft").replaceAll("_", "-")}`,
    ];
  };

  render() {
    const {
      appointmentsData,
      leavesData,
      showAppointment,
      currentAppointment,
      showLeaveModal,
      selectedLeave,
      showLeaves,
      date,
      calendarStartHour,
      calendarEndHour,
    } = this.state;

    const combinedEvents = showLeaves
      ? [...appointmentsData, ...leavesData]
      : appointmentsData;
    const calendarEvents = combinedEvents.map((event: any) => {
      const isAllDay = event.is_all_day === true || event.isAllDay === true;
      const rawEnd = event.endDateTime || event.end_date_time;
      const end =
        isAllDay && rawEnd
          ? moment(rawEnd).add(1, "day").format("YYYY-MM-DD")
          : rawEnd;

      return {
        ...event,
        appointmentId: event.id ?? event.appointmentId ?? event.appointment_id,
        start: event.startDateTime || event.start_date_time,
        end,
        allDay: isAllDay,
        title: this.formatTitle(event),
      };
    });
    const dateTitle = new Intl.DateTimeFormat("en", {
      month: "long",
      year: "numeric",
    }).format(date);
    const timeString = (hour: number) =>
      `${String(hour).padStart(2, "0")}:00:00`;

    return (
      <>
        <main className="service-calendar mx-auto w-full max-w-7xl px-3 pt-1 pb-5 sm:px-6">
          <header className="mb-3 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-sky-700">
                Dyno schedule
              </p>
              <h1 className="m-0 text-2xl font-semibold tracking-normal text-slate-950">
                {dateTitle}
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center rounded-md border border-slate-300 bg-white p-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => this.changeCalendarDate("prev")}
                  aria-label="Previous period"
                  title="Previous period"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => this.changeCalendarDate("today")}
                  aria-label="Today"
                  title="Today"
                >
                  <CalendarDays className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => this.changeCalendarDate("next")}
                  aria-label="Next period"
                  title="Next period"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center rounded-md border border-slate-300 bg-white p-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => this.changeCalendarView("dayGridMonth")}
                  aria-label="Month view"
                  title="Month view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => this.changeCalendarView("timeGridWeek")}
                  aria-label="Week view"
                  title="Week view"
                >
                  <CalendarDays className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => this.changeCalendarView("listWeek")}
                  aria-label="List view"
                  title="List view"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <Link
                to="/list"
                aria-label="Search appointments"
                title="Search appointments"
              >
                <Button variant="outline" size="icon">
                  <Search className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </header>
          <section className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
            <FullCalendar
              ref={this.calendarRef}
              plugins={[
                dayGridPlugin,
                timeGridPlugin,
                listPlugin,
                interactionPlugin,
              ]}
              initialView="dayGridMonth"
              headerToolbar={false}
              height="calc(100vh - 210px)"
              expandRows
              dayMaxEvents
              nowIndicator
              allDaySlot={showLeaves}
              slotMinTime={timeString(calendarStartHour)}
              slotMaxTime={timeString(calendarEndHour)}
              scrollTime={timeString(calendarStartHour)}
              events={calendarEvents}
              eventClassNames={(info) =>
                this.eventClassNames(info.event.extendedProps)
              }
              eventClick={(info) =>
                this.showModal({
                  ...info.event.extendedProps,
                  id:
                    info.event.extendedProps.appointmentId ||
                    info.event.id ||
                    info.event.extendedProps.id,
                })
              }
              datesSet={(info) =>
                this.viewChanged(info.view.calendar.getDate())
              }
            />
          </section>
        </main>
        <Appointment
          show={showAppointment}
          onHide={this.hideModal}
          appointment={currentAppointment}
          onAppointmentChange={this.onAppointmentChanged}
        />
        <LeaveModal
          show={showLeaveModal}
          onHide={() =>
            this.setState({ showLeaveModal: false, selectedLeave: null })
          }
          leave={selectedLeave}
          onLeaveChanged={this.onAppointmentChanged}
          filterType="dyno"
        />
      </>
    );
  }
}
